import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Pencil, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  MapPin, 
  Clock, 
  Phone, 
  Mail, 
  Globe,
  Search,
  Filter,
  Edit3
} from 'lucide-react';
import { useResources } from '@/contexts/ResourceContext';
import type { Resource, ResourceCategory } from '@/contexts/ResourceContext';
import { cn } from '@/lib/utils';

const categoryConfig: Record<ResourceCategory, { label: string; icon: string; color: string }> = {
  food: { label: 'Food & Groceries', icon: '🍎', color: '#22c55e' },
  shelter: { label: 'Shelter & Housing', icon: '🏠', color: '#8b5cf6' },
  medical: { label: 'Health Care', icon: '⚕️', color: '#ef4444' },
  skills: { label: 'Skills & Learning', icon: '📚', color: '#3b82f6' },
  tools: { label: 'Tools & Equipment', icon: '🔧', color: '#f59e0b' },
  transport: { label: 'Transportation', icon: '🚗', color: '#06b6d4' },
  legal: { label: 'Legal Aid', icon: '⚖️', color: '#ec4899' },
  childcare: { label: 'Childcare', icon: '👶', color: '#f97316' },
  elderlycare: { label: 'Elder Care', icon: '👵', color: '#6366f1' },
  'mental-health': { label: 'Mental Health', icon: '💜', color: '#a855f7' },
  community: { label: 'Community', icon: '🌱', color: '#14b8a6' },
  other: { label: 'Other', icon: '✨', color: '#6b7280' },
};

interface EditResourceDialogProps {
  resource: Resource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EditResourceDialog({ resource, open, onOpenChange }: EditResourceDialogProps) {
  const { updateResource } = useResources();
  const [formData, setFormData] = useState({
    name: resource.name,
    category: resource.category,
    description: resource.description,
    address: resource.address,
    latitude: resource.latitude,
    longitude: resource.longitude,
    phone: resource.contact?.phone || '',
    email: resource.contact?.email || '',
    website: resource.contact?.website || '',
    hours: resource.hours || '',
    tags: resource.tags.join(', '),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateResource(resource.id, {
      name: formData.name,
      category: formData.category as ResourceCategory,
      description: formData.description,
      address: formData.address,
      latitude: formData.latitude,
      longitude: formData.longitude,
      contact: {
        phone: formData.phone || undefined,
        email: formData.email || undefined,
        website: formData.website || undefined,
      },
      hours: formData.hours || undefined,
      tags: formData.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Resource
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Resource Name</Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-category">Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value as ResourceCategory })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.icon} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-address">Address</Label>
            <Input
              id="edit-address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Latitude</Label>
              <Input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Longitude</Label>
              <Input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-hours">Hours</Label>
            <Input
              id="edit-hours"
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Email</Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Website</Label>
            <Input
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Tags (comma separated)</Label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="free, community, verified"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditResourcesTab() {
  const { resources, deleteResource, verifyResource } = useResources();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVerified, setFilterVerified] = useState<'all' | 'verified' | 'unverified'>('all');
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [deletingResource, setDeletingResource] = useState<Resource | null>(null);

  const filteredResources = resources.filter(resource => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!resource.name.toLowerCase().includes(query) && 
          !resource.description.toLowerCase().includes(query) &&
          !resource.address.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Verified filter
    if (filterVerified === 'verified' && !resource.verified) return false;
    if (filterVerified === 'unverified' && resource.verified) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Manage Resources</h2>
          <p className="text-muted-foreground">
            Edit, delete, or verify community resources
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredResources.length} of {resources.length} resources
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterVerified === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterVerified('all')}
          >
            <Filter className="h-4 w-4 mr-1" />
            All
          </Button>
          <Button
            variant={filterVerified === 'verified' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterVerified('verified')}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Verified
          </Button>
          <Button
            variant={filterVerified === 'unverified' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterVerified('unverified')}
          >
            <XCircle className="h-4 w-4 mr-1" />
            Unverified
          </Button>
        </div>
      </div>

      {/* Resource List */}
      {filteredResources.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground">
              No resources found matching your criteria
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredResources.map((resource) => {
            const config = categoryConfig[resource.category];
            return (
              <Card 
                key={resource.id} 
                className={cn(
                  "transition-all hover:shadow-md",
                  !resource.verified && "border-l-4 border-l-amber-400"
                )}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Resource Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{resource.name}</h3>
                        {resource.verified ? (
                          <Badge variant="default" className="bg-green-500 text-white text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Unverified
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {resource.address}
                        </span>
                        {resource.hours && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {resource.hours}
                          </span>
                        )}
                        {resource.contact?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {resource.contact.phone}
                          </span>
                        )}
                        {resource.contact?.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {resource.contact.email}
                          </span>
                        )}
                        {resource.contact?.website && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            Website
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge 
                          variant="secondary" 
                          className="text-xs"
                          style={{ 
                            backgroundColor: `${config.color}20`, 
                            color: config.color 
                          }}
                        >
                          {config.icon} {config.label}
                        </Badge>
                        {resource.tags.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex sm:flex-col gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingResource(resource)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      {!resource.verified && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => verifyResource(resource.id)}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Verify</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeletingResource(resource)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      {editingResource && (
        <EditResourceDialog
          resource={editingResource}
          open={!!editingResource}
          onOpenChange={(open) => !open && setEditingResource(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingResource} onOpenChange={(open) => !open && setDeletingResource(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Resource</DialogTitle>
          </DialogHeader>
          <p className="py-4">
            Are you sure you want to delete <strong>{deletingResource?.name}</strong>? 
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingResource(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (deletingResource) {
                  deleteResource(deletingResource.id);
                  setDeletingResource(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}